import type { TenantResumen, TipoDeComprobante } from '../api';
import { nombreDeTenant } from '../tenants';
import { TIPOS_DE_COMPROBANTE } from './dominio';
import type { FiltroLocal } from './filtros';
import { conCambio } from './filtros';

/**
 * Los filtros del listado (KAN-146): contribuyente, rango de fechas y tipo.
 *
 * **No hay botón "Buscar".** Cada cambio consulta, porque son todos controles
 * discretos (dos selectores y dos fechas completas): no hay un campo de texto
 * donde cada tecla dispararía un pedido.
 *
 * El contribuyente **no viene preseleccionado**: es obligatorio en el contrato,
 * y elegir uno por la persona mostraría la facturación de un contribuyente que
 * nadie pidió ver, con el nombre del que sí pidió en ninguna parte. Se elige
 * por razón social **y CUIT** ({@link nombreDeTenant}) porque dos clientes
 * pueden llamarse casi igual y el CUIT es lo que se puede verificar contra un
 * papel.
 */
export function FiltrosDeComprobantes({
  tenants,
  filtro,
  alCambiar,
}: {
  /** Los tenants que esta credencial puede leer. */
  tenants: readonly TenantResumen[];
  filtro: FiltroLocal;
  alCambiar: (filtro: FiltroLocal) => void;
}) {
  return (
    <fieldset>
      <legend>Filtros</legend>

      <p>
        <label htmlFor="filtro-tenant">Contribuyente</label>
        <br />
        <select
          id="filtro-tenant"
          value={filtro.tenantId === null ? '' : String(filtro.tenantId)}
          onChange={(e) =>
            alCambiar(conCambio(filtro, { tenantId: e.target.value === '' ? null : Number(e.target.value) }))
          }
        >
          <option value="">Elegí un contribuyente…</option>
          {tenants.map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {nombreDeTenant(tenant)}
            </option>
          ))}
        </select>
      </p>

      <p>
        <label htmlFor="filtro-desde">Desde</label>{' '}
        <input
          id="filtro-desde"
          type="date"
          value={filtro.desde}
          onChange={(e) => alCambiar(conCambio(filtro, { desde: e.target.value }))}
        />{' '}
        <label htmlFor="filtro-hasta">Hasta</label>{' '}
        <input
          id="filtro-hasta"
          type="date"
          value={filtro.hasta}
          onChange={(e) => alCambiar(conCambio(filtro, { hasta: e.target.value }))}
        />
        <br />
        <span className="nota">
          Las dos fechas son inclusive y se comparan contra la fecha del comprobante, en zona
          argentina.
        </span>
      </p>

      <p>
        <label htmlFor="filtro-tipo">Tipo</label>
        <br />
        <select
          id="filtro-tipo"
          value={filtro.tipo === null ? '' : String(filtro.tipo)}
          onChange={(e) =>
            alCambiar(
              conCambio(filtro, {
                // El `as` es el precio de que `<select>` hable en strings: los
                // valores salen de TIPOS_DE_COMPROBANTE, que sí está tipado
                // contra el contrato, así que acá no se puede colar un tipo que
                // el backend no acepte.
                tipo: e.target.value === '' ? null : (Number(e.target.value) as TipoDeComprobante),
              }),
            )
          }
        >
          <option value="">Todos</option>
          {TIPOS_DE_COMPROBANTE.map((tipo) => (
            <option key={tipo.id} value={tipo.id}>
              {tipo.etiqueta}
            </option>
          ))}
        </select>
      </p>
    </fieldset>
  );
}
